import * as BABYLON from '@babylonjs/core';

/**
 * Modular Scaffolding System - Ringlock Style
 * Thin Instances for performance, pipe-only structure
 * Supports multiple connected scaffolds with edge merging
 */

const STANDARD_DIAMETER = 0.048;
const LEDGER_DIAMETER = 0.042;
const ROSETTE_DIAMETER = 0.12;
const ROSETTE_THICKNESS = 0.012;
const BASEJACK_SIZE = 0.15;
const DECK_THICKNESS = 0.038;
const STAIR_TREAD_DEPTH = 0.25;
const CW_SIZE = 0.9; // 900mm cube counterweight

// Material specifications for load calculations
const MATERIAL_SPECS = {
  aluminium: {
    label: 'Aluminium',
    pipeWeight: 1.5,       // kg/m (48.3mm OD)
    ledgerWeight: 1.2,     // kg/m (42mm OD)
    rosetteWeight: 0.3,    // kg each
    baseJackWeight: 1.5,   // kg each
    deckWeight: 15,        // kg/m²
    legCapacity: 2000,     // kg ultimate per leg
    lateralCapacity: 150,  // kg per connection point
  },
  steel: {
    label: 'Steel',
    pipeWeight: 3.2,
    ledgerWeight: 2.5,
    rosetteWeight: 0.8,
    baseJackWeight: 3.0,
    deckWeight: 25,
    legCapacity: 4000,
    lateralCapacity: 300,
  }
};
const OSHA_FACTOR = 4;
const BAY_SIZE = 1.0;           // meters per bay (ringlock module)
const LEG_EFFICIENCY = 0.7;     // load distribution factor (weakest leg governs)
const LIVE_LOAD_MARGIN = 1.1;   // self-weight safety margin
const SIDE_EFFICIENCY = 0.6;    // effective lateral connection factor (no bracing)

const STAIR_ROTATIONS = {
  front: 0,
  right: Math.PI / 2,
  back: Math.PI,
  left: -Math.PI / 2
};

const DEFAULT_DECK = { width: null, depth: null, alignH: 'center', alignD: 'center', shiftX: 0, shiftZ: 0 };

/** Get all deck configs for a level (array of decks, backward compatible) */
function getDeckLevelConfigs(cfg, level) {
  const dl = cfg.deckLevels?.[String(level)];
  if (!dl) return [{ ...DEFAULT_DECK }];
  if (Array.isArray(dl)) return dl.length > 0 ? dl : [{ ...DEFAULT_DECK }];
  return [dl];
}

function getStairLevelConfig(cfg, level) {
  const sl = cfg.stairLevels?.[String(level)];
  return {
    rotation: sl?.rotation || 'front',
    shiftX: sl?.shiftX || 0,
    shiftZ: sl?.shiftZ || 0
  };
}

/**
 * Calculate the world-space origin (corner 0,0) of a support scaffold
 * relative to the primary scaffold's center.
 * The grids align at 1m intervals so shared edges merge cleanly.
 */
function calcSupportOrigin(primary, sup) {
  const pW = Math.max(1, Math.round(primary.width || 3));
  const pD = Math.max(1, Math.round(primary.depth || 2));
  const sW = Math.max(1, Math.round(sup.width || 2));
  const sD = Math.max(1, Math.round(sup.depth || 2));
  const dist = Math.max(0, Math.round(sup.distance || 0));

  // Primary grid runs from (-pW/2, -pD/2) to (pW/2, pD/2)
  // Primary's grid corner (ix=0, iz=0) is at (-pW/2, -pD/2)
  const pCornerX = -pW / 2;
  const pCornerZ = -pD / 2;

  let ox, oz; // origin of support in world space (its ix=0, iz=0 corner)

  const align = sup.alignEdge || 'center'; // left / center / right

  switch (sup.side) {
    case 'back': {
      oz = pCornerZ + pD + dist; // support front edge at primary back edge + distance
      if (align === 'left') ox = pCornerX;
      else if (align === 'right') ox = pCornerX + pW - sW;
      else ox = pCornerX + Math.round((pW - sW) / 2); // center
      break;
    }
    case 'front': {
      oz = pCornerZ - dist - sD; // support extends in -Z
      if (align === 'left') ox = pCornerX;
      else if (align === 'right') ox = pCornerX + pW - sW;
      else ox = pCornerX + Math.round((pW - sW) / 2);
      break;
    }
    case 'right': {
      ox = pCornerX + pW + dist; // support left edge at primary right edge + distance
      if (align === 'left') oz = pCornerZ;
      else if (align === 'right') oz = pCornerZ + pD - sD;
      else oz = pCornerZ + Math.round((pD - sD) / 2);
      break;
    }
    case 'left': {
      ox = pCornerX - dist - sW; // support extends in -X
      if (align === 'left') oz = pCornerZ;
      else if (align === 'right') oz = pCornerZ + pD - sD;
      else oz = pCornerZ + Math.round((pD - sD) / 2);
      break;
    }
    default:
      oz = pCornerZ + pD + dist;
      ox = pCornerX + Math.round((pW - sW) / 2);
  }

  return { ox, oz };
}

/**
 * Collect all structural matrices from a single scaffold unit
 * in world-space coords for later dedup.
 * origin = {ox, oz} is the (ix=0, iz=0) corner in world space.
 */
function collectScaffoldStructure(cfg, ox, oz, maps) {
  const W = Math.max(1, Math.round(cfg.width || 3));
  const D = Math.max(1, Math.round(cfg.depth || 2));
  const H = Math.max(1, Math.round(cfg.height || 3));

  // Standards (vertical pipes): key = "x,z" → max height
  for (let ix = 0; ix <= W; ix++) {
    for (let iz = 0; iz <= D; iz++) {
      const x = ox + ix;
      const z = oz + iz;
      const key = `${x.toFixed(2)},${z.toFixed(2)}`;
      const existing = maps.standards.get(key);
      if (!existing || H > existing.H) {
        maps.standards.set(key, { x, z, H });
      }
    }
  }

  // Ledgers X (horizontal along X-axis, between adjacent standards in X): key = "x,y,z"
  for (let level = 0; level <= H; level++) {
    for (let iz = 0; iz <= D; iz++) {
      for (let ix = 0; ix < W; ix++) {
        const x = ox + ix + 0.5;
        const z = oz + iz;
        const key = `lx:${x.toFixed(2)},${level},${z.toFixed(2)}`;
        if (!maps.ledgersX.has(key)) {
          maps.ledgersX.set(key, { x, y: level, z });
        }
      }
    }
  }

  // Ledgers Z (horizontal along Z-axis)
  for (let level = 0; level <= H; level++) {
    for (let ix = 0; ix <= W; ix++) {
      for (let iz = 0; iz < D; iz++) {
        const x = ox + ix;
        const z = oz + iz + 0.5;
        const key = `lz:${x.toFixed(2)},${level},${z.toFixed(2)}`;
        if (!maps.ledgersZ.has(key)) {
          maps.ledgersZ.set(key, { x, y: level, z });
        }
      }
    }
  }

  // Rosettes: at every standard x every level
  for (let ix = 0; ix <= W; ix++) {
    for (let iz = 0; iz <= D; iz++) {
      for (let level = 0; level <= H; level++) {
        const x = ox + ix;
        const z = oz + iz;
        const key = `r:${x.toFixed(2)},${level},${z.toFixed(2)}`;
        if (!maps.rosettes.has(key)) {
          maps.rosettes.set(key, { x, y: level, z });
        }
      }
    }
  }

  // Base jacks
  for (let ix = 0; ix <= W; ix++) {
    for (let iz = 0; iz <= D; iz++) {
      const x = ox + ix;
      const z = oz + iz;
      const key = `bj:${x.toFixed(2)},${z.toFixed(2)}`;
      if (!maps.baseJacks.has(key)) {
        maps.baseJacks.set(key, { x, z });
      }
    }
  }
}

/**
 * Collect deck matrices for a single scaffold unit
 */
function collectDeckMatrices(cfg, ox, oz, deckMatrices) {
  const W = Math.max(1, Math.round(cfg.width || 3));
  const D = Math.max(1, Math.round(cfg.depth || 2));
  const H = Math.max(1, Math.round(cfg.height || 3));
  const showDecks = cfg.showDecks || [];

  for (const level of showDecks) {
    if (level < 1 || level > H) continue;
    const configs = getDeckLevelConfigs(cfg, level);
    for (const lc of configs) {
      const dw = lc.width != null ? Math.max(1, Math.min(W, Math.round(lc.width))) : W;
      const dd = lc.depth != null ? Math.max(1, Math.min(D, Math.round(lc.depth))) : D;

      let localOx;
      if (lc.alignH === 'left') localOx = 0;
      else if (lc.alignH === 'right') localOx = W - dw;
      else localOx = (W - dw) / 2;

      let localOz;
      if (lc.alignD === 'front') localOz = 0;
      else if (lc.alignD === 'back') localOz = D - dd;
      else localOz = (D - dd) / 2;

      const shX = (lc.shiftX || 0) / 1000;
      const shZ = (lc.shiftZ || 0) / 1000;

      for (let ix = 0; ix < dw; ix++) {
        for (let iz = 0; iz < dd; iz++) {
          deckMatrices.push(BABYLON.Matrix.Compose(
            new BABYLON.Vector3(0.96, 1, 0.96),
            BABYLON.Quaternion.Identity(),
            new BABYLON.Vector3(ox + localOx + ix + 0.5 + shX, level + DECK_THICKNESS / 2, oz + localOz + iz + 0.5 + shZ)
          ));
        }
      }
    }
  }
}

/**
 * Collect stair tread matrices for a single scaffold unit
 */
function collectStairMatrices(cfg, ox, oz, treadMatrices) {
  const H = Math.max(1, Math.round(cfg.height || 3));
  const showStairs = cfg.showStairs || [];

  for (const level of showStairs) {
    if (level < 1 || level > H) continue;
    const lc = getStairLevelConfig(cfg, level);
    const rotY = STAIR_ROTATIONS[lc.rotation] || 0;
    const rotQuat = BABYLON.Quaternion.RotationAxis(BABYLON.Axis.Y, rotY);
    const baseY = level - 1;
    const sinR = Math.sin(rotY);
    const cosR = Math.cos(rotY);

    const shX = (lc.shiftX || 0) / 1000;
    const shZ = (lc.shiftZ || 0) / 1000;
    const cellX = ox + 0.5 + shX;
    const cellZ = oz + 0.5 + shZ;

    const numTreads = 4;
    for (let t = 0; t < numTreads; t++) {
      const frac = (t + 0.5) / numTreads;
      const forwardOffset = (frac - 0.5) * 1.0;
      treadMatrices.push(BABYLON.Matrix.Compose(
        new BABYLON.Vector3(1, 1, 1),
        rotQuat,
        new BABYLON.Vector3(
          cellX + forwardOffset * (-sinR),
          baseY + frac,
          cellZ + forwardOffset * cosR
        )
      ));
    }
  }
}

/**
 * Collect top railing matrices for a single scaffold unit
 */
function collectRailingMatrices(cfg, ox, oz, railMatrices) {
  const W = Math.max(1, Math.round(cfg.width || 3));
  const D = Math.max(1, Math.round(cfg.depth || 2));
  const H = Math.max(1, Math.round(cfg.height || 3));
  const topRailing = cfg.topRailing || {};
  const railSides = ['front', 'back', 'left', 'right'].filter(s => topRailing[s]);
  if (railSides.length === 0) return;

  const RAIL_H = 1.0;
  const topY = H;

  for (const side of railSides) {
    if (side === 'front') {
      const z = oz;
      for (let ix = 0; ix <= W; ix++) {
        const x = ox + ix;
        railMatrices.push(BABYLON.Matrix.Compose(
          new BABYLON.Vector3(1, RAIL_H, 1), BABYLON.Quaternion.Identity(),
          new BABYLON.Vector3(x, topY + RAIL_H / 2, z)
        ));
      }
      for (let ix = 0; ix < W; ix++) {
        railMatrices.push(BABYLON.Matrix.Compose(
          new BABYLON.Vector3(1, 1, 1),
          BABYLON.Quaternion.RotationAxis(BABYLON.Axis.Z, Math.PI / 2),
          new BABYLON.Vector3(ox + ix + 0.5, topY + RAIL_H, z)
        ));
        railMatrices.push(BABYLON.Matrix.Compose(
          new BABYLON.Vector3(1, 1, 1),
          BABYLON.Quaternion.RotationAxis(BABYLON.Axis.Z, Math.PI / 2),
          new BABYLON.Vector3(ox + ix + 0.5, topY + RAIL_H * 0.5, z)
        ));
      }
    }
    if (side === 'back') {
      const z = oz + D;
      for (let ix = 0; ix <= W; ix++) {
        railMatrices.push(BABYLON.Matrix.Compose(
          new BABYLON.Vector3(1, RAIL_H, 1), BABYLON.Quaternion.Identity(),
          new BABYLON.Vector3(ox + ix, topY + RAIL_H / 2, z)
        ));
      }
      for (let ix = 0; ix < W; ix++) {
        railMatrices.push(BABYLON.Matrix.Compose(
          new BABYLON.Vector3(1, 1, 1),
          BABYLON.Quaternion.RotationAxis(BABYLON.Axis.Z, Math.PI / 2),
          new BABYLON.Vector3(ox + ix + 0.5, topY + RAIL_H, z)
        ));
        railMatrices.push(BABYLON.Matrix.Compose(
          new BABYLON.Vector3(1, 1, 1),
          BABYLON.Quaternion.RotationAxis(BABYLON.Axis.Z, Math.PI / 2),
          new BABYLON.Vector3(ox + ix + 0.5, topY + RAIL_H * 0.5, z)
        ));
      }
    }
    if (side === 'left') {
      const x = ox;
      for (let iz = 0; iz <= D; iz++) {
        railMatrices.push(BABYLON.Matrix.Compose(
          new BABYLON.Vector3(1, RAIL_H, 1), BABYLON.Quaternion.Identity(),
          new BABYLON.Vector3(x, topY + RAIL_H / 2, oz + iz)
        ));
      }
      for (let iz = 0; iz < D; iz++) {
        railMatrices.push(BABYLON.Matrix.Compose(
          new BABYLON.Vector3(1, 1, 1),
          BABYLON.Quaternion.RotationAxis(BABYLON.Axis.X, Math.PI / 2),
          new BABYLON.Vector3(x, topY + RAIL_H, oz + iz + 0.5)
        ));
        railMatrices.push(BABYLON.Matrix.Compose(
          new BABYLON.Vector3(1, 1, 1),
          BABYLON.Quaternion.RotationAxis(BABYLON.Axis.X, Math.PI / 2),
          new BABYLON.Vector3(x, topY + RAIL_H * 0.5, oz + iz + 0.5)
        ));
      }
    }
    if (side === 'right') {
      const x = ox + W;
      for (let iz = 0; iz <= D; iz++) {
        railMatrices.push(BABYLON.Matrix.Compose(
          new BABYLON.Vector3(1, RAIL_H, 1), BABYLON.Quaternion.Identity(),
          new BABYLON.Vector3(x, topY + RAIL_H / 2, oz + iz)
        ));
      }
      for (let iz = 0; iz < D; iz++) {
        railMatrices.push(BABYLON.Matrix.Compose(
          new BABYLON.Vector3(1, 1, 1),
          BABYLON.Quaternion.RotationAxis(BABYLON.Axis.X, Math.PI / 2),
          new BABYLON.Vector3(x, topY + RAIL_H, oz + iz + 0.5)
        ));
        railMatrices.push(BABYLON.Matrix.Compose(
          new BABYLON.Vector3(1, 1, 1),
          BABYLON.Quaternion.RotationAxis(BABYLON.Axis.X, Math.PI / 2),
          new BABYLON.Vector3(x, topY + RAIL_H * 0.5, oz + iz + 0.5)
        ));
      }
    }
  }
}

/**
 * Collect counterweight positions for a single scaffold unit
 */
function collectCounterweightPositions(cfg, ox, oz, cwPositions) {
  const W = Math.max(1, Math.round(cfg.width || 3));
  const D = Math.max(1, Math.round(cfg.depth || 2));
  const counterweights = cfg.counterweights || [];
  for (const cw of counterweights) {
    const gx = cw.gridX;
    const gz = cw.gridZ;
    if (gx < 0 || gx >= W || gz < 0 || gz >= D) continue;
    const key = `${(ox + gx + 0.5).toFixed(2)},${(oz + gz + 0.5).toFixed(2)}`;
    if (!cwPositions.has(key)) {
      cwPositions.set(key, { x: ox + gx + 0.5, z: oz + gz + 0.5 });
    }
  }
}

/**
 * Calculate scaffold load data for the entire structure (primary + supports)
 */
export function calculateScaffoldLoad(config) {
  const matType = config.materialType || 'aluminium';
  const specs = MATERIAL_SPECS[matType] || MATERIAL_SPECS.aluminium;

  let totalStandards = 0, totalStdHeight = 0;
  let totalLedgersX = 0, totalLedgersZ = 0;
  let totalRosettes = 0, totalBaseJacks = 0, totalDeckArea = 0;

  const allUnits = [config, ...(config.supportScaffolds || [])];
  for (const sc of allUnits) {
    const W = Math.max(1, Math.round(sc.width || 3));
    const D = Math.max(1, Math.round(sc.depth || 2));
    const H = Math.max(1, Math.round(sc.height || 3));
    const numStd = (W + 1) * (D + 1);
    totalStandards += numStd;
    totalStdHeight += numStd * H;
    totalLedgersX += W * (D + 1) * (H + 1);
    totalLedgersZ += (W + 1) * D * (H + 1);
    totalRosettes += numStd * (H + 1);
    totalBaseJacks += numStd;
    const showDecks = sc.showDecks || [];
    for (const lvl of showDecks) {
      if (lvl < 1 || lvl > H) continue;
      const dls = getDeckLevelConfigs(sc, lvl);
      for (const dc of dls) {
        const dw = dc.width != null ? Math.min(W, Math.max(1, dc.width)) : W;
        const dd = dc.depth != null ? Math.min(D, Math.max(1, dc.depth)) : D;
        totalDeckArea += dw * dd;
      }
    }
  }

  const wStd = totalStdHeight * BAY_SIZE * specs.pipeWeight;
  const wLX = totalLedgersX * BAY_SIZE * specs.ledgerWeight;
  const wLZ = totalLedgersZ * BAY_SIZE * specs.ledgerWeight;
  const wR = totalRosettes * specs.rosetteWeight;
  const wBJ = totalBaseJacks * specs.baseJackWeight;
  const wDeck = totalDeckArea * specs.deckWeight;
  const totalWeight = wStd + wLX + wLZ + wR + wBJ + wDeck;

  let cwKg = 0;
  for (const sc of allUnits) cwKg += (sc.counterweights?.length || 0) * 1000;

  // Top Load: effective legs × capacity, then OSHA, then subtract self-weight with margin
  const ultimateTop = totalStandards * LEG_EFFICIENCY * specs.legCapacity;
  const safeTop = ultimateTop / OSHA_FACTOR;
  const availableTop = Math.max(0, safeTop - (totalWeight * LIVE_LOAD_MARGIN));

  // Side Load: effective connections per unit, summed across all units
  const sideCapacities = { front: 0, back: 0, left: 0, right: 0 };
  for (const sc of allUnits) {
    const W = Math.max(1, Math.round(sc.width || 3));
    const D = Math.max(1, Math.round(sc.depth || 2));
    const H = Math.max(1, Math.round(sc.height || 3));
    const fbCap = Math.round(W * H * SIDE_EFFICIENCY * specs.lateralCapacity / OSHA_FACTOR);
    const lrCap = Math.round(D * H * SIDE_EFFICIENCY * specs.lateralCapacity / OSHA_FACTOR);
    sideCapacities.front += fbCap;
    sideCapacities.back += fbCap;
    sideCapacities.left += lrCap;
    sideCapacities.right += lrCap;
  }

  // Counterweight bonus: CW × (baseWidth / height) × 0.5 ÷ OSHA
  if (cwKg > 0) {
    const pW = Math.max(1, Math.round(config.width || 3));
    const pD = Math.max(1, Math.round(config.depth || 2));
    const pH = Math.max(1, Math.round(config.height || 3));
    const cwBonusFB = Math.round(cwKg * (pW / pH) * 0.5 / OSHA_FACTOR);
    const cwBonusLR = Math.round(cwKg * (pD / pH) * 0.5 / OSHA_FACTOR);
    sideCapacities.front += cwBonusFB;
    sideCapacities.back += cwBonusFB;
    sideCapacities.left += cwBonusLR;
    sideCapacities.right += cwBonusLR;
  }

  return {
    materialType: matType, specs,
    components: { standards: totalStandards, stdHeight: totalStdHeight, ledgersX: totalLedgersX, ledgersZ: totalLedgersZ, rosettes: totalRosettes, baseJacks: totalBaseJacks, deckArea: totalDeckArea },
    weights: { standards: wStd, ledgersX: wLX, ledgersZ: wLZ, rosettes: wR, baseJacks: wBJ, decks: wDeck, total: totalWeight, counterweight: cwKg },
    topLoad: { ultimate: ultimateTop, safe: safeTop, available: Math.round(availableTop) },
    sideCapacities,
    oshaFactor: OSHA_FACTOR,
    totalPipes: totalStandards + totalLedgersX + totalLedgersZ,
  };
}

/** Get load ratio color: green < 0.7, yellow 0.7-1.0, red > 1.0 */
function getLoadColor(ratio) {
  if (ratio <= 0) return null;
  if (ratio > 1.0) return new BABYLON.Color3(0.8, 0.05, 0.05);
  if (ratio > 0.7) return new BABYLON.Color3(0.85, 0.75, 0.0);
  return new BABYLON.Color3(0.0, 0.55, 0.0);
}

/**
 * Main entry: build the full scaffolding system (primary + supports)
 */
export function buildScaffolding(config, objId, scene) {
  const pW = Math.max(1, Math.round(config.width || 3));
  const pD = Math.max(1, Math.round(config.depth || 2));
  const pH = Math.max(1, Math.round(config.height || 3));

  const structureColor = config.structureColor || '#C0C0C0';
  const deckColor = config.deckColor || '#D9D9D9';

  const root = new BABYLON.TransformNode('scaffolding_' + objId, scene);
  root.metadata = { id: objId, type: 'object', isScaffolding: true };

  const structCol = BABYLON.Color3.FromHexString(structureColor);
  const deckCol = BABYLON.Color3.FromHexString(deckColor);

  const structMat = new BABYLON.PBRMaterial('scaffStruct_' + objId, scene);
  structMat.albedoColor = structCol;
  structMat.metallic = 0.7;
  structMat.roughness = 0.35;
  structMat.backFaceCulling = false;
  structMat.alpha = 1.0;

  const deckMat = new BABYLON.PBRMaterial('scaffDeck_' + objId, scene);
  deckMat.albedoColor = deckCol;
  deckMat.metallic = 0.1;
  deckMat.roughness = 0.8;
  deckMat.backFaceCulling = false;

  // --- Load-based visual highlighting on decks ---
  const loadInputs = config.loadInputs || {};
  const loadData = calculateScaffoldLoad(config);
  const showHL = config.showLoadHighlights !== false;
  if (showHL && loadInputs.topLoad > 0 && loadData.topLoad.available > 0) {
    const topRatio = loadInputs.topLoad / loadData.topLoad.available;
    const topColor = getLoadColor(topRatio);
    if (topColor) deckMat.emissiveColor = topColor;
  }

  // --- Master meshes ---
  const standardMaster = BABYLON.MeshBuilder.CreateCylinder('scaffStdM_' + objId, {
    height: 1.0, diameter: STANDARD_DIAMETER, tessellation: 8
  }, scene);
  standardMaster.material = structMat;
  standardMaster.parent = root;
  standardMaster.metadata = { scaffoldMaster: true };

  const ledgerXMaster = BABYLON.MeshBuilder.CreateCylinder('scaffLXM_' + objId, {
    height: 1.0, diameter: LEDGER_DIAMETER, tessellation: 8
  }, scene);
  ledgerXMaster.material = structMat;
  ledgerXMaster.parent = root;
  ledgerXMaster.metadata = { scaffoldMaster: true };

  const ledgerZMaster = BABYLON.MeshBuilder.CreateCylinder('scaffLZM_' + objId, {
    height: 1.0, diameter: LEDGER_DIAMETER, tessellation: 8
  }, scene);
  ledgerZMaster.material = structMat;
  ledgerZMaster.parent = root;
  ledgerZMaster.metadata = { scaffoldMaster: true };

  const rosetteMaster = BABYLON.MeshBuilder.CreateCylinder('scaffRM_' + objId, {
    height: ROSETTE_THICKNESS, diameter: ROSETTE_DIAMETER, tessellation: 8
  }, scene);
  rosetteMaster.material = structMat;
  rosetteMaster.parent = root;
  rosetteMaster.metadata = { scaffoldMaster: true };

  const baseJackMaster = BABYLON.MeshBuilder.CreateBox('scaffBJM_' + objId, {
    width: BASEJACK_SIZE, height: 0.005, depth: BASEJACK_SIZE
  }, scene);
  baseJackMaster.material = structMat;
  baseJackMaster.parent = root;
  baseJackMaster.metadata = { scaffoldMaster: true };

  // ===== Collect all scaffold units =====
  // Primary scaffold origin: center at (0,0), corner at (-pW/2, -pD/2)
  const primaryOrigin = { ox: -pW / 2, oz: -pD / 2 };

  // Dedup maps for structural elements
  const maps = {
    standards: new Map(),  // key "x,z" → {x, z, H}
    ledgersX: new Map(),   // key "lx:x,y,z" → {x, y, z}
    ledgersZ: new Map(),   // key "lz:x,y,z" → {x, y, z}
    rosettes: new Map(),   // key "r:x,y,z" → {x, y, z}
    baseJacks: new Map()   // key "bj:x,z" → {x, z}
  };

  // Collect primary structure
  collectScaffoldStructure(config, primaryOrigin.ox, primaryOrigin.oz, maps);

  // Collect support scaffolds
  const supports = config.supportScaffolds || [];
  for (const sup of supports) {
    const origin = calcSupportOrigin(config, sup);
    collectScaffoldStructure(sup, origin.ox, origin.oz, maps);
  }

  // ===== Convert dedup maps to matrices =====
  // Standards
  const stdMats = [];
  for (const { x, z, H } of maps.standards.values()) {
    stdMats.push(BABYLON.Matrix.Compose(
      new BABYLON.Vector3(1, H, 1),
      BABYLON.Quaternion.Identity(),
      new BABYLON.Vector3(x, H / 2, z)
    ));
  }

  // Ledgers X
  const lxMats = [];
  for (const { x, y, z } of maps.ledgersX.values()) {
    lxMats.push(BABYLON.Matrix.Compose(
      new BABYLON.Vector3(1, 1, 1),
      BABYLON.Quaternion.RotationAxis(BABYLON.Axis.Z, Math.PI / 2),
      new BABYLON.Vector3(x, y, z)
    ));
  }

  // Ledgers Z
  const lzMats = [];
  for (const { x, y, z } of maps.ledgersZ.values()) {
    lzMats.push(BABYLON.Matrix.Compose(
      new BABYLON.Vector3(1, 1, 1),
      BABYLON.Quaternion.RotationAxis(BABYLON.Axis.X, Math.PI / 2),
      new BABYLON.Vector3(x, y, z)
    ));
  }

  // Rosettes
  const rMats = [];
  for (const { x, y, z } of maps.rosettes.values()) {
    rMats.push(BABYLON.Matrix.Compose(
      new BABYLON.Vector3(1, 1, 1),
      BABYLON.Quaternion.Identity(),
      new BABYLON.Vector3(x, y, z)
    ));
  }

  // Base jacks
  const bjMats = [];
  for (const { x, z } of maps.baseJacks.values()) {
    bjMats.push(BABYLON.Matrix.Compose(
      new BABYLON.Vector3(1, 1, 1),
      BABYLON.Quaternion.Identity(),
      new BABYLON.Vector3(x, -0.003, z)
    ));
  }

  applyThinInstances(standardMaster, stdMats);
  applyThinInstances(ledgerXMaster, lxMats);
  applyThinInstances(ledgerZMaster, lzMats);
  applyThinInstances(rosetteMaster, rMats);
  applyThinInstances(baseJackMaster, bjMats);

  // ===== Decks (primary + supports) =====
  const deckMatrices = [];
  collectDeckMatrices(config, primaryOrigin.ox, primaryOrigin.oz, deckMatrices);
  for (const sup of supports) {
    const origin = calcSupportOrigin(config, sup);
    collectDeckMatrices(sup, origin.ox, origin.oz, deckMatrices);
  }
  if (deckMatrices.length > 0) {
    const deckMaster = BABYLON.MeshBuilder.CreateBox('scaffDkM_' + objId, {
      width: 1.0, height: DECK_THICKNESS, depth: 1.0
    }, scene);
    deckMaster.material = deckMat;
    deckMaster.parent = root;
    deckMaster.metadata = { scaffoldMaster: true };
    applyThinInstances(deckMaster, deckMatrices);
  }

  // ===== Stairs (primary + supports) =====
  const treadMatrices = [];
  collectStairMatrices(config, primaryOrigin.ox, primaryOrigin.oz, treadMatrices);
  for (const sup of supports) {
    const origin = calcSupportOrigin(config, sup);
    collectStairMatrices(sup, origin.ox, origin.oz, treadMatrices);
  }
  if (treadMatrices.length > 0) {
    const treadMaster = BABYLON.MeshBuilder.CreateBox('scaffTdM_' + objId, {
      width: 0.9, height: 0.025, depth: STAIR_TREAD_DEPTH
    }, scene);
    treadMaster.material = deckMat;
    treadMaster.parent = root;
    treadMaster.metadata = { scaffoldMaster: true };
    applyThinInstances(treadMaster, treadMatrices);
  }

  // ===== Top Railings (primary + supports) =====
  const railMatrices = [];
  collectRailingMatrices(config, primaryOrigin.ox, primaryOrigin.oz, railMatrices);
  for (const sup of supports) {
    const origin = calcSupportOrigin(config, sup);
    collectRailingMatrices(sup, origin.ox, origin.oz, railMatrices);
  }
  if (railMatrices.length > 0) {
    const RAIL_DIAM = 0.035;
    const railPipeMaster = BABYLON.MeshBuilder.CreateCylinder('scaffTopRailM_' + objId, {
      height: 1.0, diameter: RAIL_DIAM, tessellation: 8
    }, scene);
    railPipeMaster.material = structMat;
    railPipeMaster.parent = root;
    railPipeMaster.metadata = { scaffoldMaster: true };
    applyThinInstances(railPipeMaster, railMatrices);
  }

  // ===== Counterweights (primary + supports) =====
  const cwColor = config.counterweightColor || '#A0A0A0';
  const showCWLabels = config.showCWLabels !== false;
  const cwPositions = new Map();
  collectCounterweightPositions(config, primaryOrigin.ox, primaryOrigin.oz, cwPositions);
  for (const sup of supports) {
    const origin = calcSupportOrigin(config, sup);
    collectCounterweightPositions(sup, origin.ox, origin.oz, cwPositions);
  }
  if (cwPositions.size > 0) {
    const cwCol = BABYLON.Color3.FromHexString(cwColor);
    const cwMat = new BABYLON.PBRMaterial('scaffCW_' + objId, scene);
    cwMat.albedoColor = cwCol;
    cwMat.metallic = 0.05;
    cwMat.roughness = 0.85;
    cwMat.backFaceCulling = false;

    const cwMaster = BABYLON.MeshBuilder.CreateBox('scaffCWM_' + objId, {
      width: CW_SIZE, height: CW_SIZE, depth: CW_SIZE
    }, scene);
    cwMaster.material = cwMat;
    cwMaster.parent = root;
    cwMaster.metadata = { scaffoldMaster: true };

    const cwMats = [];
    for (const { x, z } of cwPositions.values()) {
      cwMats.push(BABYLON.Matrix.Compose(
        new BABYLON.Vector3(1, 1, 1),
        BABYLON.Quaternion.Identity(),
        new BABYLON.Vector3(x, CW_SIZE / 2, z)
      ));
    }
    applyThinInstances(cwMaster, cwMats);

    // Labels ("1000 KG" on top face)
    if (showCWLabels) {
      const labelTex = new BABYLON.DynamicTexture('cwLabelTex_' + objId, { width: 256, height: 256 }, scene, false);
      const ctx = labelTex.getContext();
      ctx.fillStyle = 'rgba(0,0,0,0)';
      ctx.clearRect(0, 0, 256, 256);
      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 42px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('1000 KG', 128, 128);
      labelTex.update();
      labelTex.hasAlpha = true;

      const labelMat = new BABYLON.StandardMaterial('cwLabelMat_' + objId, scene);
      labelMat.diffuseTexture = labelTex;
      labelMat.emissiveColor = new BABYLON.Color3(0.3, 0.3, 0.3);
      labelMat.useAlphaFromDiffuseTexture = true;
      labelMat.backFaceCulling = false;

      const labelMaster = BABYLON.MeshBuilder.CreatePlane('scaffCWLblM_' + objId, {
        width: 0.75, height: 0.75
      }, scene);
      labelMaster.material = labelMat;
      labelMaster.parent = root;
      labelMaster.metadata = { scaffoldMaster: true };

      const lblMats = [];
      for (const { x, z } of cwPositions.values()) {
        lblMats.push(BABYLON.Matrix.Compose(
          new BABYLON.Vector3(1, 1, 1),
          BABYLON.Quaternion.RotationAxis(BABYLON.Axis.X, -Math.PI / 2),
          new BABYLON.Vector3(x, CW_SIZE + 0.002, z)
        ));
      }
      applyThinInstances(labelMaster, lblMats);
    }
  }

  // ===== Side Load Visual Highlights =====
  const sideLoadSides = loadInputs.sideLoadSides || {};
  const sideLoadKg = loadInputs.sideLoad || 0;
  if (showHL && sideLoadKg > 0) {
    for (const side of ['front', 'back', 'left', 'right']) {
      if (!sideLoadSides[side]) continue;
      const cap = loadData.sideCapacities[side];
      if (cap <= 0) continue;
      const ratio = sideLoadKg / cap;
      const sColor = getLoadColor(ratio);
      if (!sColor) continue;

      const sideMat = new BABYLON.StandardMaterial('scaffSideHL_' + side + '_' + objId, scene);
      sideMat.diffuseColor = sColor;
      sideMat.emissiveColor = sColor;
      sideMat.alpha = 0.25;
      sideMat.backFaceCulling = false;

      let sw, sh, sd, sx, sz;
      const pOx = primaryOrigin.ox, pOz = primaryOrigin.oz;
      if (side === 'front') { sw = pW; sd = 0.08; sh = pH; sx = pOx + pW / 2; sz = pOz; }
      else if (side === 'back') { sw = pW; sd = 0.08; sh = pH; sx = pOx + pW / 2; sz = pOz + pD; }
      else if (side === 'left') { sw = 0.08; sd = pD; sh = pH; sx = pOx; sz = pOz + pD / 2; }
      else { sw = 0.08; sd = pD; sh = pH; sx = pOx + pW; sz = pOz + pD / 2; }

      const hlMesh = BABYLON.MeshBuilder.CreateBox('scaffSideHL_' + side + '_' + objId, { width: sw, height: sh, depth: sd }, scene);
      hlMesh.position = new BABYLON.Vector3(sx, sh / 2, sz);
      hlMesh.material = sideMat;
      hlMesh.parent = root;
      hlMesh.isPickable = false;
      hlMesh.metadata = { scaffoldMaster: true };
    }
  }

  // ===== Bounding box for picking =====
  // Calculate bounds encompassing primary + all supports
  let minX = primaryOrigin.ox, maxX = primaryOrigin.ox + pW;
  let minZ = primaryOrigin.oz, maxZ = primaryOrigin.oz + pD;
  let maxH = pH;
  for (const sup of supports) {
    const origin = calcSupportOrigin(config, sup);
    const sW = Math.max(1, Math.round(sup.width || 2));
    const sD = Math.max(1, Math.round(sup.depth || 2));
    const sH = Math.max(1, Math.round(sup.height || 2));
    minX = Math.min(minX, origin.ox);
    maxX = Math.max(maxX, origin.ox + sW);
    minZ = Math.min(minZ, origin.oz);
    maxZ = Math.max(maxZ, origin.oz + sD);
    maxH = Math.max(maxH, sH);
  }
  const boundW = maxX - minX;
  const boundD = maxZ - minZ;
  const boundCX = (minX + maxX) / 2;
  const boundCZ = (minZ + maxZ) / 2;

  const boundingMesh = BABYLON.MeshBuilder.CreateBox('scaffBound_' + objId, {
    width: boundW, height: maxH, depth: boundD
  }, scene);
  boundingMesh.position = new BABYLON.Vector3(boundCX, maxH / 2, boundCZ);
  boundingMesh.visibility = 0;
  boundingMesh.isPickable = true;
  boundingMesh.metadata = { id: objId, type: 'object', scaffoldHelper: true };
  boundingMesh.parent = root;

  root.isPickable = true;
  return root;
}

function applyThinInstances(master, matrices) {
  if (matrices.length === 0) return;
  const buf = new Float32Array(16 * matrices.length);
  for (let i = 0; i < matrices.length; i++) {
    matrices[i].copyToArray(buf, i * 16);
  }
  master.thinInstanceSetBuffer('matrix', buf, 16);
  master.thinInstanceCount = matrices.length;
  master.isPickable = false;
}

export function hasScaffoldConfigChanged(prev, next) {
  if (!prev || !next) return true;
  return (
    prev.width !== next.width ||
    prev.depth !== next.depth ||
    prev.height !== next.height ||
    prev.structureColor !== next.structureColor ||
    prev.deckColor !== next.deckColor ||
    JSON.stringify(prev.showDecks || []) !== JSON.stringify(next.showDecks || []) ||
    JSON.stringify(prev.showStairs || []) !== JSON.stringify(next.showStairs || []) ||
    JSON.stringify(prev.deckLevels || {}) !== JSON.stringify(next.deckLevels || {}) ||
    JSON.stringify(prev.stairLevels || {}) !== JSON.stringify(next.stairLevels || {}) ||
    JSON.stringify(prev.topRailing || {}) !== JSON.stringify(next.topRailing || {}) ||
    JSON.stringify(prev.supportScaffolds || []) !== JSON.stringify(next.supportScaffolds || []) ||
    JSON.stringify(prev.counterweights || []) !== JSON.stringify(next.counterweights || []) ||
    prev.counterweightColor !== next.counterweightColor ||
    prev.showCWLabels !== next.showCWLabels ||
    prev.materialType !== next.materialType ||
    JSON.stringify(prev.loadInputs || {}) !== JSON.stringify(next.loadInputs || {}) ||
    prev.showLoadHighlights !== next.showLoadHighlights
  );
}
